import { useState, useMemo } from "react";
import { Check, Image as ImageIcon, Video, FileCode, Layout, Search, Eye, X, CheckCircle, Clock, XCircle, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";

// Creative Approval Badge
function CreativeApprovalBadge({ status }) {
  const config = {
    approved: { icon: CheckCircle, class: "bg-green-100 text-green-700 border-green-200", label: "Approved" },
    pending: { icon: Clock, class: "bg-amber-100 text-amber-700 border-amber-200", label: "Pending" },
    rejected: { icon: XCircle, class: "bg-red-100 text-red-700 border-red-200", label: "Rejected" }
  };
  
  const { icon: Icon, class: className, label } = config[status] || config.pending;
  
  return (
    <Badge variant="outline" className={`${className} font-medium text-[10px] tracking-wider px-1.5 py-0 flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
}

export function CreativesStep({ form, updateField, creatives = [] }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [previewCreative, setPreviewCreative] = useState(null);

  // Ensure creatives is always an array
  const safeCreatives = Array.isArray(creatives) ? creatives : [];

  // Sort creatives by created_at descending (newest first) and filter by search
  const filteredCreatives = useMemo(() => {
    let sorted = [...safeCreatives].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA; // Descending order
    });

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      sorted = sorted.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.type?.toLowerCase().includes(query) ||
        (c.banner_data?.width && c.banner_data?.height && 
          `${c.banner_data.width}x${c.banner_data.height}`.includes(query))
      );
    }

    return sorted;
  }, [safeCreatives, searchQuery]);

  // Count unapproved selected creatives
  const unapprovedSelectedCount = useMemo(() => {
    return form.creative_ids.filter(id => {
      const creative = safeCreatives.find(c => c.id === id);
      return creative && creative.approval_status !== 'approved';
    }).length;
  }, [form.creative_ids, safeCreatives]);

  const toggleCreative = (creativeId) => {
    const isSelected = form.creative_ids.includes(creativeId);
    
    if (isSelected) {
      const newIds = form.creative_ids.filter(id => id !== creativeId);
      updateField("creative_ids", newIds);
      if (form.creative_id === creativeId) {
        updateField("creative_id", newIds.length > 0 ? newIds[0] : "");
      }
    } else {
      const newIds = [...form.creative_ids, creativeId];
      updateField("creative_ids", newIds);
      if (!form.creative_id) {
        updateField("creative_id", creativeId);
      }
    }
  };

  const getCreativeIcon = (type) => {
    switch (type) {
      case 'video': return Video;
      case 'native': return Layout;
      case 'js_tag': return FileCode;
      default: return ImageIcon;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'video': return "bg-purple-100 text-purple-700 border-purple-200";
      case 'native': return "bg-amber-100 text-amber-700 border-amber-200";
      case 'js_tag': return "bg-pink-100 text-pink-700 border-pink-200";
      default: return "bg-blue-100 text-blue-700 border-blue-200";
    }
  };

  const getCreativeSize = (creative) => {
    if (creative.banner_data?.width && creative.banner_data?.height) {
      return `${creative.banner_data.width}x${creative.banner_data.height}`;
    }
    if (creative.video_data?.width && creative.video_data?.height) {
      return `${creative.video_data.width}x${creative.video_data.height}`;
    }
    if (creative.native_data?.main_image_width && creative.native_data?.main_image_height) {
      return `${creative.native_data.main_image_width}x${creative.native_data.main_image_height}`;
    }
    return "-";
  };

  const getPreviewUrl = (creative) => {
    if (creative.banner_data?.image_url) return creative.banner_data.image_url;
    if (creative.video_data?.video_url) return creative.video_data.video_url;
    if (creative.video_data?.thumbnail_url) return creative.video_data.thumbnail_url;
    if (creative.native_data?.main_image_url) return creative.native_data.main_image_url;
    return null;
  };

  const selectedCount = form.creative_ids.length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 mb-1">Creatives</h2>
        <p className="text-sm text-slate-500">Select creatives for your campaign (newest first)</p>
      </div>

      {/* Search and Selection Info */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, type, or size..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 border-slate-200"
            data-testid="creative-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="text-sm text-slate-600">
          {selectedCount > 0 ? (
            <span className="text-[#10B981] font-medium">{selectedCount} selected</span>
          ) : (
            <span>No creatives selected</span>
          )}
        </div>
      </div>

      {/* Warning for unapproved creatives */}
      {unapprovedSelectedCount > 0 && (
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              {unapprovedSelectedCount} selected creative{unapprovedSelectedCount > 1 ? 's are' : ' is'} not approved
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Campaign will not serve ads until all selected creatives are approved by Super Admin.
            </p>
          </div>
        </div>
      )}

      {creatives.length === 0 ? (
        <Card className="surface-secondary border-slate-200">
          <CardContent className="py-12 text-center">
            <ImageIcon className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-2">No creatives available</p>
            <p className="text-sm text-slate-500">
              Create creatives in the Creatives section first
            </p>
          </CardContent>
        </Card>
      ) : filteredCreatives.length === 0 ? (
        <Card className="surface-secondary border-slate-200">
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-2">No creatives match your search</p>
            <p className="text-sm text-slate-500">
              Try a different search term
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="surface-primary border-slate-200">
          <CardContent className="p-0">
            {/* Table Header */}
            <div className="grid grid-cols-[40px_1fr_100px_100px_80px_80px] gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
              <div></div>
              <div>Creative Name</div>
              <div>Size</div>
              <div>Type</div>
              <div>Status</div>
              <div className="text-center">Preview</div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
              {filteredCreatives.map((creative) => {
                const isSelected = form.creative_id === creative.id || form.creative_ids.includes(creative.id);
                const Icon = getCreativeIcon(creative.type);
                const size = getCreativeSize(creative);
                const isRejected = creative.approval_status === 'rejected';
                
                return (
                  <div
                    key={creative.id}
                    className={`grid grid-cols-[40px_1fr_100px_100px_80px_80px] gap-4 px-4 py-3 items-center transition-colors ${
                      isSelected ? "bg-[#10B981]/5" : isRejected ? "bg-red-50/50" : "hover:bg-slate-50"
                    }`}
                    data-testid="creative-row"
                  >
                    {/* Checkbox */}
                    <div>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleCreative(creative.id)}
                        className="border-slate-300 data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981]"
                        data-testid="creative-checkbox"
                        disabled={isRejected}
                      />
                    </div>

                    {/* Creative Name */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "bg-[#10B981]/20" : isRejected ? "bg-red-100" : "bg-slate-100"
                      }`}>
                        <Icon className={`w-4 h-4 ${isSelected ? "text-[#10B981]" : isRejected ? "text-red-500" : "text-slate-500"}`} />
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? "text-[#10B981]" : isRejected ? "text-red-600" : "text-slate-900"}`}>
                          {creative.name}
                        </p>
                        {/* Show rejection reason */}
                        {isRejected && creative.approval_notes && (
                          <p className="text-[10px] text-red-500 truncate" title={creative.approval_notes}>
                            {creative.approval_notes}
                          </p>
                        )}
                        {!isRejected && (
                          <p className="text-xs text-slate-400 truncate font-mono">
                            {creative.id.slice(0, 8)}...
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Size */}
                    <div>
                      <span className="text-sm text-slate-600 font-mono">{size}</span>
                    </div>

                    {/* Type */}
                    <div>
                      <Badge className={`${getTypeBadgeColor(creative.type)} border text-xs`}>
                        {creative.type}
                      </Badge>
                    </div>
                    
                    {/* Approval Status */}
                    <div>
                      <CreativeApprovalBadge status={creative.approval_status || 'pending'} />
                    </div>

                    {/* Preview Button */}
                    <div className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewCreative(creative);
                        }}
                        className="h-8 w-8 p-0 text-slate-500 hover:text-[#3B82F6] hover:bg-[#3B82F6]/10"
                        data-testid="preview-creative-btn"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Table Footer */}
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
              Showing {filteredCreatives.length} of {creatives.length} creatives
            </div>
          </CardContent>
        </Card>
      )}

      {/* Best Practices */}
      <Card className="surface-secondary border-[#F59E0B]/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-[#F59E0B]">Creative Best Practices</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="text-[#F59E0B]">•</span>
              Use multiple ad sizes (300x250, 728x90, 320x50) for maximum reach
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#F59E0B]">•</span>
              Test 3-4 creative variations and optimize after 1000 impressions each
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#F59E0B]">•</span>
              Include clear call-to-action in all creatives
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#F59E0B]">•</span>
              For video: 15s or 30s formats work best; ensure sound-off compatibility
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={!!previewCreative} onOpenChange={() => setPreviewCreative(null)}>
        <DialogContent className="surface-primary border-panel sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Eye className="w-5 h-5 text-[#3B82F6]" />
              Creative Preview
            </DialogTitle>
          </DialogHeader>
          
          {previewCreative && (
            <div className="space-y-4">
              {/* Creative Info */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">{previewCreative.name}</p>
                  <p className="text-sm text-slate-500">
                    {getCreativeSize(previewCreative)} • {previewCreative.type}
                  </p>
                </div>
                <Badge className={getTypeBadgeColor(previewCreative.type)}>
                  {previewCreative.type}
                </Badge>
              </div>

              {/* Preview Area */}
              <div className="flex items-center justify-center min-h-[300px] bg-slate-100 rounded-lg overflow-hidden">
                {previewCreative.type === 'video' ? (
                  previewCreative.video_data?.video_url ? (
                    <video
                      src={previewCreative.video_data.video_url}
                      controls
                      className="max-w-full max-h-[400px]"
                      poster={previewCreative.video_data?.thumbnail_url}
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : previewCreative.video_data?.thumbnail_url ? (
                    <img 
                      src={previewCreative.video_data.thumbnail_url}
                      alt={previewCreative.name}
                      className="max-w-full max-h-[400px] object-contain"
                    />
                  ) : (
                    <div className="text-center text-slate-500">
                      <Video className="w-16 h-16 mx-auto mb-2 opacity-50" />
                      <p>No video preview available</p>
                    </div>
                  )
                ) : previewCreative.banner_data?.image_url ? (
                  <img 
                    src={previewCreative.banner_data.image_url}
                    alt={previewCreative.name}
                    className="max-w-full max-h-[400px] object-contain"
                  />
                ) : previewCreative.native_data?.main_image_url ? (
                  <img 
                    src={previewCreative.native_data.main_image_url}
                    alt={previewCreative.name}
                    className="max-w-full max-h-[400px] object-contain"
                  />
                ) : previewCreative.js_tag_data?.tag_content ? (
                  <div className="p-4 w-full">
                    <p className="text-sm text-slate-500 mb-2">JS Tag Content:</p>
                    <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-[300px]">
                      {previewCreative.js_tag_data.tag_content}
                    </pre>
                  </div>
                ) : (
                  <div className="text-center text-slate-500">
                    <ImageIcon className="w-16 h-16 mx-auto mb-2 opacity-50" />
                    <p>No preview available</p>
                  </div>
                )}
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-slate-500 text-xs uppercase mb-1">Creative ID</p>
                  <p className="font-mono text-slate-700 truncate">{previewCreative.id}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-slate-500 text-xs uppercase mb-1">Format</p>
                  <p className="text-slate-700">{previewCreative.format || previewCreative.type}</p>
                </div>
                {previewCreative.click_url && (
                  <div className="p-3 bg-slate-50 rounded-lg col-span-2">
                    <p className="text-slate-500 text-xs uppercase mb-1">Click URL</p>
                    <p className="font-mono text-xs text-slate-700 truncate">{previewCreative.click_url}</p>
                  </div>
                )}
              </div>

              {/* Select Button */}
              <div className="flex justify-end pt-2">
                <Button
                  onClick={() => {
                    toggleCreative(previewCreative.id);
                    setPreviewCreative(null);
                  }}
                  className={form.creative_ids.includes(previewCreative.id) 
                    ? "bg-slate-200 text-slate-700 hover:bg-slate-300" 
                    : "bg-[#10B981] hover:bg-[#059669] text-white"
                  }
                >
                  {form.creative_ids.includes(previewCreative.id) ? (
                    <>
                      <X className="w-4 h-4 mr-2" />
                      Deselect Creative
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Select Creative
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
